import { IStore } from "../DataTypes/storeTypes";
import * as ActionGenerics from "../../../Common/actionGenerics";
import { Actions } from "./actionTypes";
import {
    ICardInfo,
    IIdempotencyKeyModel,
    ISetupSCAModel,
    ISetupSCAResponse,
    IStripeFormData,
} from "../../../Common/Stripe/types";
import { get, post } from "../../../Common/fetch";
import { error } from "../../../Common/Notifications/helper";
import { StripeState } from "../DataTypes/stripeTypes";
import { setIsBusy } from "./actions";
import { GuidProvider } from "common.ui/Helpers/guidProvider";
import { Stripe, StripeElements } from "@stripe/stripe-js";
import { CardNumberElement } from "@stripe/react-stripe-js";

function ShowError(dispatch: any, message: string) {
    error(message);
    dispatch(setIsBusy(false));
}

export function showStripeDialog(updateFieldCallback: (value) => void) {
    return (dispatch, getState: () => IStore) => {
        const state = getState();

        if (!state.initialSettings.clientStripeAccount) {
            ShowError(dispatch, "Unable to add card details. Please contact the clinic for assistance.");
            return;
        }

        dispatch(setIsBusy(true));

        dispatch(updateState(StripeState.Show));

        dispatch(setUpdateFieldCallback(updateFieldCallback));

        getCreditCard(dispatch, state)
            .then(() => dispatch(setIsBusy(false)))
            .catch(() => setIsBusy(false));
    };
}

function setUpdateFieldCallback(
    updateFieldCallback: (value) => void
): ActionGenerics.IParamAction<Actions, (value) => void> {
    return { type: Actions.SetUpdateFieldCallback, parameter: updateFieldCallback };
}

export function updateState(state: StripeState): ActionGenerics.IParamAction<Actions, StripeState> {
    return { type: Actions.UpdateStripeState, parameter: state };
}

export function editCreditCard(isEdit: boolean): ActionGenerics.IParamAction<Actions, boolean> {
    return { type: Actions.EditCreditCard, parameter: isEdit };
}

export function setIdempotencyKeyAction(key: string): ActionGenerics.IParamAction<Actions, string> {
    return { type: Actions.SetIdempotencyKey, parameter: key };
}

export function setIdempotencyKey() {
    return (dispatch, getState: () => IStore) => {
        const state = getState();

        if (state.stripe.idempotencyKey) {
            return;
        }

        dispatch(setIdempotencyKeyAction(GuidProvider.getGuid()));
    };
}

export function stripePay(values: IStripeFormData, stripe: Stripe, elements: StripeElements) {
    return async (dispatch, getState: () => IStore) => {
        const state = getState();
        const cardElement = elements.getElement(CardNumberElement);

        dispatch(setIsBusy(true));

        try {
            const createTokenResult = await stripe.createToken(cardElement);
            if (createTokenResult?.error) {
                throw new Error(createTokenResult.error.message);
            }

            const saveCardModel: IIdempotencyKeyModel = {
                idempotencyKey: state.stripe.idempotencyKey,
            };

            const setupCardResult = await post<ISetupSCAResponse>(state.initialSettings.setupSCACardUrl, saveCardModel);
            const confirmCardSetupResult = await stripe.confirmCardSetup(setupCardResult.clientSecret, {
                payment_method: {
                    card: cardElement,
                },
            });
            if (confirmCardSetupResult?.error) {
                throw new Error(confirmCardSetupResult.error.message);
            }

            const model: ISetupSCAModel = {
                paymentMethodId: confirmCardSetupResult.setupIntent.payment_method,
                practitionerId: state.initialSettings.practitionerId,
                saveDetails: true,
            };

            const updateCustomerResult = await post<any>(state.initialSettings.createOrUpdateCustomer, model);
            if (updateCustomerResult?.error) {
                throw new Error(updateCustomerResult.error);
            }

            dispatch(updateState(StripeState.None));
            dispatch(setIsBusy(false));
            state.stripe.updateFieldCallback("Card Saved");
        } catch ({ message }) {
            ShowError(dispatch, message);
        }
    };
}

function updateCreditCard(card: ICardInfo): ActionGenerics.IParamAction<Actions, ICardInfo> {
    return { type: Actions.UpdateCreditCard, parameter: card };
}

export function getCreditCard(dispatch, state: IStore) {
    return get<ICardInfo>(state.initialSettings.getCreditCardUrl)
        .then((data) => dispatch(updateCreditCard(data)))
        .catch((data) => error(data.message));
}
